import { WindowLocation } from '@reach/router';
import { parse } from 'query-string';
import { get, isNumber, isString } from 'lodash';
import DOMPurify from 'isomorphic-dompurify';
import linkifyHtml from 'linkifyjs/html';

import { FormComponentType, IElementSchema, Optional } from '../../types/form';
import { IStage } from '../../types/models/program-management';
import { findDataPathForFormSchema } from '../form';
import { DocumentType } from '../../types/models/document';
import { DOCUMENT_UPLOAD_ENROLLMENT_VERIFICATION_FORM, DOCUMENT_UPLOAD_FAAL_FORM } from '../form-builder';
import { FAALReadOnlyFields } from '../../components/admin/program/stages/stage-document-verification/forms/read-only/faal';
import { EVReadOnlyFields } from '../../types/models/document-verification';

export function getTextContentFromHTML(htmlContent = '') {
  if (!htmlContent) {
    return '';
  }
  return htmlContent
    .replace(/(<([^>]+)>)/gi, ' ')
    .replace(/\s\s+/g, ' ')
    .replace(/\s+([!|?|.|,|;|:|...])/g, '$1')
    .trim();
}

const replaceHTMLEntities = (text: string) => {
  const entitiesPattern = /&(nbsp|amp|quot|lt|gt);/g;
  const entitiesMapper: any = {
    nbsp: ' ',
    amp: '&',
    quot: '"',
    lt: '<',
    gt: '>',
  };

  return text.replace(entitiesPattern, (_, entity) => entitiesMapper[entity]);
};

export function getRawUserInputFromHTML(htmlContent = '') {
  const inputText = htmlContent.replace(/(<([^>]+)>)/gi, '');

  return replaceHTMLEntities(inputText);
}

export function isNonEmptyString(value: any) {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Checks if number is greater than thero.
 *
 * @param {Optional<number>} value The number to be tested
 * @param {boolean} includeZero Should function include zero or not
 * @returns {boolean}
 */
export const isPositiveNumber = (value: Optional<number>, includeZero = true) => {
  return isNumber(value) && value >= (includeZero ? 0 : 1);
};

/**
 * Checks for empty string value. Whitespace will be removed.
 *
 * @param {Optional<string>} value The string to be tested
 * @returns {boolean}
 */
export const isEmptyString = (value: Optional<string>) => {
  return !value || (typeof value === 'string' && !value.trim().length);
};

/**
 * Check if url is valid.
 *
 * @param {Optional<string>} url The string to be tested
 * @returns {boolean}
 */
export const isValidUrl = (url: Optional<string>): boolean => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i'
  );

  return !!url && pattern.test(url);
};

/**
 * Check if url has protocol.
 *
 * @param {Optional<string>} url The string to be tested
 * @returns {boolean}
 */
export const hasUrlProtocol = (url?: string): boolean =>
  !!url && (url.startsWith('http://') || url.startsWith('https://'));

/**
 * Returns valid link href or empty string.
 *
 * @param {string} [url] The string to be tested
 * @returns {string}
 */
export const getValidLinkHref = (url?: string): string => {
  if (!url) {
    return '';
  }

  if (!hasUrlProtocol(url)) {
    return `https://${url}`;
  }

  return url;
};

/**
 * Truncate the string based on the desired character limits
 * @param textContent
 * @param maxLength
 */
export const getTextValueWithCharLimits = (textContent: string | undefined, maxLength: number) => {
  if (typeof textContent === 'string' && textContent.length > maxLength) {
    return textContent.substr(0, maxLength);
  }
  return textContent;
};

/**
 * Format raw US 10 digit phone number
 *
 * @export
 * @param {string} value
 * @returns
 */
export function formatPhoneNumber(value: string) {
  const cleaned = ('' + value).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return ['(', match[1], ') ', match[2], '-', match[3]].join('');
  }
  return value;
}

/**
 * Cleans the tag name
 *
 * @example
 * "Abc&* (22" -> "Abc 22"
 *
 * @param {string} label The string to be cleaned
 * @returns {string}
 */
export const cleanTagName = (name: string): string => {
  return name
    .replace(/[^a-z0-9-.\s+#]/gi, '') // supported characters a-z 0-9 + # - . \s
    .trim()
    .substr(0, 35);
};

export function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export function hasFormComponent(stage: IStage, targetComponent: FormComponentType) {
  if (stage.formSchema) {
    return !!findDataPathForFormSchema(stage.formSchema, {
      type: targetComponent,
    });
  }
  return false;
}

export function parseUrlQueryParams(searchString?: string): any {
  const result: any = {};

  if (!searchString) {
    return result;
  }

  const chunks = searchString.split('?')[1].split('&');

  chunks.forEach((chunk) => {
    const keyValue = chunk.split('=');
    result[keyValue[0]] = keyValue[1];
  });

  return result;
}

export const capitalize = (str: string): string => str.replace(/\b\w/g, (c) => c.toUpperCase());

export const addEllipsis = (text: string, cutOff: number) =>
  text.length > cutOff ? `${text.slice(0, cutOff)}...` : text;

export function getQueryParam(key: string, location?: WindowLocation): string | null | undefined {
  const qs = location ? get(parse(location.search), key) : null;
  if (Array.isArray(qs)) {
    return qs[0];
  }
  return qs;
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

export const formatCurrencyString = (currencyString: string) => {
  const wordsPrefix = currencyString?.match(/^[\w][\s\D]*/g);
  const wordsEndPrefix = currencyString?.match(/[\s][\D]*$/g);
  const matchedValues = currencyString?.match(/[\d]+/g);
  if (matchedValues) {
    return matchedValues?.reduce((result, value, index, initial) => {
      const fValue = currencyFormatter.format(Number(value));
      if (!(index % 2)) {
        result += fValue;
      } else {
        result += `-${fValue}${index + 1 === initial.length ? '' : ', '}`;
      }
      if (wordsPrefix && wordsPrefix[0] !== '1' && wordsPrefix[0] !== '2') {
        result = `${wordsPrefix} ${result}`;
      }
      if (wordsEndPrefix) {
        result = `${result} ${wordsEndPrefix}`;
      }
      return result;
    }, '');
  }
  return currencyString;
};

export const isHtml = (str: string) => {
  return /<\/?[a-z][\s\S]*>/i.test(str);
};

export const getHtmlWithClickableLinks = (body?: any): string => {
  if (!isString(body)) {
    return '';
  }

  return DOMPurify.sanitize(linkifyHtml(body, { defaultProtocol: 'https' }), {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href', 'target'],
  });
};

/**
 * @param {string | Array} ethnicity
 * @param {Object} identities
 * @returns {string}
 */
export const transformEthnicity = (ethnicity: string | string[], identities: any = {}): string => {
  let result = '-';

  if (!ethnicity || !ethnicity.length) {
    return result;
  }

  if (typeof ethnicity === 'string') {
    return identities[ethnicity] ? identities[ethnicity].displayText : result;
  }

  if (Array.isArray(ethnicity)) {
    if (ethnicity.length > 1) {
      result = ethnicity.reduce((res: any, item: any, i: number) => {
        if (identities[item]) {
          res += i !== ethnicity.length - 1 ? `${identities[item].displayText}, ` : `${identities[item].displayText}`;
        }
        return res;
      }, '');
    } else {
      result = identities[ethnicity[0]] ? identities[ethnicity[0]].displayText : result;
    }
  }

  return result;
};

export const isUUID = (str: string) => {
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(str);
};

export const isPhoneE164Format = (str: string) => {
  const regex = /^\+[1-9]\d{1,14}$/;
  return regex.test(str);
};

export const isEmail = (str: string) => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(str);
};

export const getDefaultDataSource = (docType: DocumentType) => {
  let elementSchema: IElementSchema[] | null = null;
  let field = '';

  if (docType === DocumentType.FINANCIAL_AID_AWARD_LETTER) {
    elementSchema = DOCUMENT_UPLOAD_FAAL_FORM;
    field = FAALReadOnlyFields.INSTITUTION_NAME;
  } else if (docType === DocumentType.ENROLLMENT_VERIFICATION) {
    elementSchema = DOCUMENT_UPLOAD_ENROLLMENT_VERIFICATION_FORM;
    field = EVReadOnlyFields.UNIVERSITY_NAME;
  }

  return elementSchema?.find((component) => component.name === field)?.dataSource;
};
